import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';

import { importFile, openAnnotations, OpenedAnnotations } from '@jax-data-science/sci-image-visualizer';

import { MainState } from '../main/main.state';
import { FileFolder, IFile, IFileInfo, ImageInfo, Project, RawFileInfo } from '../main/models/file-info';
import { FilesService } from './files.service';

/** A file that may be annotations opened by themselves (a folder may be too: a GeoJSON folder, a zarr store). */
const ANNOTATION_FILE = /\.(nii|nii\.gz|nrrd|zip|geojson)$/i;
const LABEL_VOLUME = /\.(nii|nii\.gz|nrrd)$/i;
/** The ontology a label volume is read with, beside it. */
const ONTOLOGY = /^(regionset\.json|structure_graph[^/]*\.json)$/i;
/** Downloads in flight at once. */
const PARALLEL = 8;

/**
 * Opens annotations by themselves, with no image under them (the file tree's "Open as
 * annotations"): a label volume (`.nii`, `.nii.gz`, `.nrrd`, with a `regionset.json` or Allen
 * `structure_graph*.json` beside it), a `.zip` or `.geojson`, or a folder (per-slice GeoJSON, a
 * label volume with its ontology, an OME-Zarr store). The files are downloaded through the auth
 * interceptor and handed to the library's `openAnnotations`, which builds the image they are
 * shown on and carries them as its regions; it is published as any image is.
 *
 * Mirrors {@link SpatialOmicsService}'s hand-off: the context menu marks the node, and the file
 * viewer opens it when the node is displayed, with the visualizer mounted.
 */
@Injectable({ providedIn: 'root' })
export class AnnotationsService {
  /** One-shot "open this node as annotations" intent, consumed when it is next displayed. */
  private pending: IFileInfo | null = null;
  /** The image last opened, released (its blob URLs) when another replaces it. */
  private opened: OpenedAnnotations | null = null;
  /** Bumped by every open and clear: an open that finds it changed was superseded. */
  private generation = 0;
  /** The open whose "Opening annotations..." overlay is up, until it ends or is dropped. */
  private loading: number | null = null;

  constructor(
    private filesService: FilesService,
    private mainState: MainState,
    private messageService: MessageService,
  ) {}

  /** Whether the context menu offers "Open as annotations" for `file`. */
  static canOpen(file: IFileInfo | null | undefined): boolean {
    return file instanceof FileFolder || (file instanceof IFile && ANNOTATION_FILE.test(file.name ?? ''));
  }

  /** Mark `file` to be opened as annotations when it is next displayed. */
  requestOpen(file: IFileInfo): void {
    this.pending = file;
  }

  /** True once for the file {@link requestOpen} marked. */
  consumeRequest(file: IFileInfo): boolean {
    const hit = this.pending === file;
    if (hit) this.pending = null;
    return hit;
  }

  /** Open `file` as annotations by themselves. Resolves whether it was shown (a message says why not). */
  async open(file: IFileInfo): Promise<boolean> {
    const generation = ++this.generation;
    this.loading = generation;
    this.mainState.setImageLoadingMessage('Opening annotations...');
    this.mainState.setImageLoading(true);
    try {
      const raws = await this.gather(file.rawData, file instanceof FileFolder);
      const files = await this.download(raws, file.rawData);
      const opened = await openAnnotations(files);
      if (generation !== this.generation) {
        opened.dispose();
        return false;
      }
      this.release();
      this.opened = opened;
      // As FilesService does for every image it publishes: the pipeline names its saves after it.
      this.mainState.setFilename(opened.info.fileName);
      this.mainState.setImageInfo$(opened.info as ImageInfo);
      // Published without FilesService's publisher, so say what is on screen: a server region
      // import browses this file's bucket, not the previous image's.
      this.filesService.setShownFile(file);
      this.endLoading(generation);
      this.messageService.add({
        key: 'center-toast',
        severity: 'success',
        summary: 'Annotations opened',
        detail: `${file.name}: ${opened.summary}`,
      });
      return true;
    } catch (err) {
      if (generation !== this.generation) return false;
      const e = err as { error?: { error?: string; message?: string }; message?: string };
      this.endLoading(generation);
      this.messageService.add({
        key: 'center-toast',
        severity: 'error',
        summary: 'Could not open the annotations',
        detail: `${file.name}: ${e?.error?.error ?? e?.error?.message ?? e?.message ?? String(err)}`,
      });
      return false;
    }
  }

  /**
   * Leave the annotations: an open in flight is dropped, along with its loading overlay (nothing
   * else would take it down if the next file opens no image), and the image's blob URLs released.
   * The file viewer calls this before it starts opening the next file, so the next open's own
   * overlay is not taken down.
   */
  clear(): void {
    this.pending = null;
    this.generation++;
    if (this.loading !== null) this.endLoading(this.loading);
    this.release();
  }

  /** Take down the loading overlay `generation` put up, if it is still that open's. */
  private endLoading(generation: number): void {
    if (this.loading !== generation) return;
    this.loading = null;
    this.mainState.setImageLoading(false);
  }

  private release(): void {
    this.opened?.dispose();
    this.opened = null;
  }

  /**
   * What to download for `raw`: a folder's every file (its subfolders too, an OME-Zarr store being
   * one), a label volume with the ontology JSON beside it, any other file alone.
   */
  private async gather(raw: RawFileInfo, folder: boolean): Promise<RawFileInfo[]> {
    if (folder) return this.filesService.listFilesDeep(raw);
    if (!LABEL_VOLUME.test(raw.name)) return [raw];
    const parent = this.parentOf(raw);
    const siblings = parent ? await firstValueFrom(this.filesService.getAllChildren$(parent)) : [];
    return [raw, ...siblings.filter((s: RawFileInfo) => s.type !== 'Folder' && ONTOLOGY.test(s.name))];
  }

  /** `raws`' bytes, a few at a time, named by their paths under `root`'s folder (as a folder pick names them). */
  private async download(raws: readonly RawFileInfo[], root: RawFileInfo) {
    const base = (this.parentOf(root)?.relPath ?? '').replace(/\/+$/, '');
    const name = (r: RawFileInfo) => {
      const rel = (r.relPath ?? r.name).replace(/^\/+/, '');
      return base && rel.startsWith(base + '/') ? rel.slice(base.length + 1) : rel;
    };
    const out = new Array(raws.length);
    let next = 0;
    const worker = async () => {
      while (next < raws.length) {
        const i = next++;
        const bytes = await firstValueFrom(this.filesService.getBytes(raws[i]));
        out[i] = importFile(new Uint8Array(bytes), name(raws[i]));
      }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL, raws.length) }, worker));
    return out;
  }

  /**
   * The folder `raw` is in, as the file service lists it: at a project's root, the project's own
   * node (as the file tree lists it, `relPath` `/`), not a nameless folder the server cannot list.
   */
  private parentOf(raw: RawFileInfo): RawFileInfo | null {
    const rel = (raw.relPath ?? '').replace(/\/+$/, '');
    const at = rel.lastIndexOf('/');
    if (at <= 0) return Project.toRawFileInfo(raw.project);
    const path = rel.substring(0, at);
    return { project: raw.project, name: path.substring(path.lastIndexOf('/') + 1), relPath: path, type: 'Folder' } as RawFileInfo;
  }
}
